import { Box, Button as MuiButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import Attachment2Icon from 'remixicon-react/Attachment2Icon';
import { ReactComponent as BlackStarsIcon } from 'assets/icons/BlackStars.svg';
import { ReactComponent as ServiceBellLineIcon } from 'assets/icons/ServiceBellLineIcon.svg';
import { Dispatch, SetStateAction, useMemo } from 'react';
import DraftLineIcon from 'remixicon-react/DraftLineIcon';
import { compact } from 'lodash';
import { BUILDWISE_MODULES_FEATURES } from 'components/drawer/constants';
import { useBuildWiseModules } from '@buildwise/libs/helpers';
import useUserData from 'hooks/useUserData';

export enum ArticleCreationMethods {
  WRITE_MANUALLY = 'Write it Yourself',
  WRITE_WITH_AI = 'Write with AI',
  PARSE_FILE = 'Summarize from File',
  WE_WILL_WRITE = 'We will Write',
}

type ArticleCreationActionsProps = {
  articleCreationMethod: ArticleCreationMethods;
  resetAllForms: () => void;
  setArticleCreationMethod: Dispatch<SetStateAction<ArticleCreationMethods>>;
};

function ArticleCreationActions({
  articleCreationMethod,
  resetAllForms,
  setArticleCreationMethod,
}: ArticleCreationActionsProps) {
  const { user } = useUserData();
  const { homeModuleFeatures } = useBuildWiseModules();
  const ACTION_BUTTONS = useMemo(
    () =>
      compact([
        homeModuleFeatures[BUILDWISE_MODULES_FEATURES.HOME.WRITE_MANUALLY]?.isActive &&
          user?.roles.some((role) =>
            homeModuleFeatures[BUILDWISE_MODULES_FEATURES.HOME.WRITE_MANUALLY].roles?.includes(
              role.roleName,
            ),
          ) && {
            icon: <DraftLineIcon size={18} rotate="45deg" />,
            displayName:
              homeModuleFeatures[BUILDWISE_MODULES_FEATURES.HOME.WRITE_MANUALLY]?.displayName ||
              homeModuleFeatures[BUILDWISE_MODULES_FEATURES.HOME.WRITE_MANUALLY]?.name,
            name: ArticleCreationMethods.WRITE_MANUALLY,
          },
        homeModuleFeatures[BUILDWISE_MODULES_FEATURES.HOME.WRITE_WITH_AI]?.isActive &&
          user?.roles.some((role) =>
            homeModuleFeatures[BUILDWISE_MODULES_FEATURES.HOME.WRITE_WITH_AI].roles?.includes(
              role.roleName,
            ),
          ) && {
            icon: <BlackStarsIcon width={18} height={18} />,
            displayName:
              homeModuleFeatures[BUILDWISE_MODULES_FEATURES.HOME.WRITE_WITH_AI]?.displayName ||
              homeModuleFeatures[BUILDWISE_MODULES_FEATURES.HOME.WRITE_WITH_AI]?.name,
            name: ArticleCreationMethods.WRITE_WITH_AI,
          },
        homeModuleFeatures[BUILDWISE_MODULES_FEATURES.HOME.PARSE_FILE]?.isActive &&
          user?.roles.some((role) =>
            homeModuleFeatures[BUILDWISE_MODULES_FEATURES.HOME.PARSE_FILE].roles?.includes(
              role.roleName,
            ),
          ) && {
            icon: <Attachment2Icon size={18} />,
            displayName:
              homeModuleFeatures[BUILDWISE_MODULES_FEATURES.HOME.PARSE_FILE]?.displayName ||
              homeModuleFeatures[BUILDWISE_MODULES_FEATURES.HOME.PARSE_FILE]?.name,
            name: ArticleCreationMethods.PARSE_FILE,
          },
        homeModuleFeatures[BUILDWISE_MODULES_FEATURES.HOME.WE_WILL_WRITE]?.isActive &&
          user?.roles.some((role) =>
            homeModuleFeatures[BUILDWISE_MODULES_FEATURES.HOME.WE_WILL_WRITE].roles?.includes(
              role.roleName,
            ),
          ) && {
            icon: <Attachment2Icon size={18} />,
            displayName:
              homeModuleFeatures[BUILDWISE_MODULES_FEATURES.HOME.WE_WILL_WRITE]?.displayName ||
              homeModuleFeatures[BUILDWISE_MODULES_FEATURES.HOME.WE_WILL_WRITE]?.name,
            name: ArticleCreationMethods.WE_WILL_WRITE,
          },
      ]),
    [homeModuleFeatures, user],
  );

  return (
    <Box display="flex" alignItems="center" justifyContent="center" mt={2} mb={0.5}>
      <Box
        sx={(theme) => ({
          backgroundColor: theme.palette.custom.whiteSmoke,
          border: `1px solid ${theme.palette.custom.line}`,
          p: 0.5,
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        })}
      >
        {ACTION_BUTTONS.map(({ icon, name, displayName }) => (
          <Button
            size="small"
            variant={articleCreationMethod === name ? 'contained' : 'text'}
            key={name}
            startIcon={icon}
            sx={(theme) => ({
              // fontSize: 14,
              ...(articleCreationMethod === name && {
                backgroundColor: theme.palette.custom.tuna,
                color: theme.palette.custom.white,
                '& svg path': {
                  fill: theme.palette.custom.white,
                },
                '&:hover': {
                  backgroundColor: theme.palette.custom.tuna,
                  color: theme.palette.custom.white,
                  boxShadow: 'none',
                },
              }),
            })}
            onClick={() => {
              setArticleCreationMethod((prevState) => {
                if (prevState !== name) {
                  resetAllForms();
                }
                return name;
              });
            }}
          >
            {displayName}
          </Button>
        ))}
      </Box>
    </Box>
  );
}

const Button = styled(MuiButton)(({ theme }) => ({
  borderRadius: 4,
  color: theme.palette.custom.tuna,
  fontWeight: 700,
}));

export default ArticleCreationActions;
